/* DCG v2-10-4 · 13 OCT 2026 · D-SP-14 · v8: the cart-page banner is also shown when the line taken out was the only one (above the empty state) · v7: the in-store-only rule covers event tickets; its message ends with "Contact us" (D-SP-35, PROPOSED) */
/**
 * dcg-cart-enforcer.js — Desert City Games  v8
 *
 * v8 change (v2-10-4, item 7):
 *   The cart-page banner (§7) is put in the cart section. When the line
 *   taken out was the only one in the cart, the page that comes back has no
 *   cart form: it shows "Your cart is currently empty." The banner was then
 *   put at the very top of <body> (`.template-cart` is the body), outside the
 *   page's content and under the fixed header, so the customer was never
 *   told why the item went. It now stands above the empty state, with its
 *   "Contact us" link, as it stands above the rows of a cart with lines.
 *   No rule is changed: what is taken out, and when, is as in v7.
 *
 * v7 changes (v2-10-3; D-SP-35 is PROPOSED until Cole locks it):
 *   1. The in-store-only rule (§3c) has no exception. A product tagged
 *      `retail` is taken out of the cart whether or not it is also tagged
 *      `event-ticket`. Cole, 11 OCT 2026: events tagged `retail` read
 *      "Register in store" on every page, so one in a cart got there by a
 *      way the theme does not offer. The zero-price rule (§3b) is unchanged:
 *      a $0.00 product tagged `event-ticket` is still "Free", not "Price
 *      pending", and is still left in the cart unless it is also `retail`.
 *   2. A message that says a product is available in store only ends with
 *      a link, "Contact us", to /pages/contact: the banner on the cart page
 *      and the message on other pages (§7). On that message the link, and
 *      only the link, can be pressed: a press anywhere else on it still
 *      goes through to the page under it, as before. With the link it stays
 *      8 seconds, and for as long as the pointer or the keyboard is on the
 *      link; every other message is as it was (5 seconds, nothing to press).
 *
 * v6 changes (v2-10-2, PROPOSED, not locked):
 *   A product tagged `retail` (in-store only) that is in the cart is taken
 *   out, and the customer is told which one and why. The theme already keeps
 *   such a product out at the card and at the product page; once in a cart
 *   (a direct link, or added before it was tagged) nothing stopped it being
 *   checked out. The rule is §3c. It runs wherever the price-pending audit
 *   runs: on every page load, on the cart page after each quantity change
 *   (auditCart), and after every cart:updated (the header cart).
 *   (v6 left a product tagged `event-ticket` as it was. v7 ends that.)
 *
 * v5 changes (v2-10-1, the cart page):
 *   Nothing is checked differently. The page-load audit (§3b then §5) is also
 *   offered as DCG.enforcer.auditCart(), returns a Promise, and announces
 *   "dcg:cart:corrected" after it has changed the cart from the cart page
 *   without a reload, so the page can draw its rows and totals again. Until
 *   now a corrected quantity stood beside the old line total and subtotal.
 *
 * Cart quantity enforcement engine. Enforces both limit:N product tags
 * and Shopify inventory stock counts across all add-to-cart paths and
 * on cart-page load. Since v4 it also keeps "price pending" ($0.00)
 * variants out of the cart.
 *
 * §1  Variant Registry   — maps variantId → { tags[], stockCap, price }
 * §2  Limit parsing      — parseLimitFromTags / parseLimitFromTagString
 * §3  Cart API helpers   — /cart.js fetch, /cart/change.js update
 * §3b Price pending      — the $0.00 rule, Shopify price lookup, cart audit
 * §4  ATC pre-flight     — called by dcg-cart-bridge.js before /cart/add.js
 * §5  Cart page audit    — auto-corrects over-limit qtys on .template-cart load
 * §6  Cart drawer audit  — best-effort correction after cart:updated events
 * §7  Message UI         — toast (global) + banner (cart page only)
 *
 * v4 changes (D-SP-14, zero-price block):
 *
 *   A variant whose price is 0 is "price pending" and cannot be ordered.
 *   Products tagged `event-ticket` are exempt (free events are $0.00 on
 *   purpose). The rule is per variant.
 *     - Registry entries carry the variant price (cents). Callers pass it as
 *       the 4th argument of registerVariant; DCG cards and the product page
 *       supply it through the attributes the registry already reads.
 *     - preflight blocks a $0.00 variant. A price the registry doesn't know
 *       is asked from Shopify (/variants/<id>.js) before the add is allowed.
 *       A failed lookup allows the add, as before.
 *     - Every page load and every cart:updated checks the cart and removes
 *       any line whose ORIGINAL unit price is 0 (so a 100% discount is never
 *       mistaken for it). This is the backstop for add paths that never call
 *       preflight.
 *     - v2-5-1: after a removal the header cart is refreshed the way
 *       dcg-cart-bridge.js does after an add (adjustCartDropDown + a
 *       cart:updated event), and every audit pass puts the header count right
 *       if it disagrees with the cart it has just read.
 *
 * v3 changes (fixes advanced search enforcement):
 *
 *   ROOT CAUSE 1 — MutationObserver never caught Phase 2 data.
 *   Advanced search cards are built in two phases by advanced_search.liquid:
 *     Phase 1 (reshapeCard): card injected into DOM, NO data-product-tags yet.
 *     Phase 2 (enrichCard):  async /products/{handle}.js fetch completes, THEN
 *                            data-product-tags, data-product-variants, and
 *                            data-variant-inventory are stamped as dataset attrs.
 *                            Phase 2 then calls dcgInitProductCards which calls
 *                            buildCardUI() which modifies the card's inner DOM.
 *   The v2 observer watched {childList:true, subtree:true}. It fired during
 *   Phase 1 (wrong — no attrs yet) and during buildCardUI (correct timing,
 *   wrong elements: addedNodes were child divs, not the attr-bearing wrapper).
 *   Fix: observer now walks UP from each addedNode to find the wrapper.
 *
 *   ROOT CAUSE 2 — Only limit tags were enforced, not stock counts.
 *   The failing test product has 6 actual inventory but no limit:N tag.
 *   Preflight found no limit → passed through → 3 + 6 = 9 in cart.
 *   Fix: registry now stores stockCap from data-variant-inventory alongside
 *   tags. preflight combines both into effectiveMax = min(limit, stockCap).
 *
 * Load order: this file is in <head> BEFORE dcg-cart-bridge.js (body/section).
 * Both use defer so <head> scripts execute first per DOM order.
 *
 * Avada Order Limit coexistence: Avada enforces at Shopify checkout level;
 * we enforce at cart-API level. No shared DOM, no shared events.
 *
 * MTG safety: issues only /cart.js, /cart/change.js and, for the price
 * lookup, /variants/<id>.js, /variants/<id> and /products/<handle>.js —
 * never touches BinderPOS or Shopify Storefront API paths.
 */

window.DCG = window.DCG || {};

window.DCG.enforcer = (function () {
  'use strict';

  /* ═══════════════════════════════════════════════════════════════════════
     §1  VARIANT REGISTRY
     Maps String(variantId) → { tags: string[], stockCap: number|null, price: number|null }
       tags     — product tag array (for limit:N enforcement)
       stockCap — max purchasable stock, or null if unconstrained.
                  Only set for Shopify-managed inventory with deny policy.
       price    — variant price in cents, or null if no caller supplied it.
                  0 means "price pending" (§3b).

     Populated by buildRegistry() at DOMContentLoaded and by the
     MutationObserver as cards are enriched by Phase 2 (enrichCard).
     ═══════════════════════════════════════════════════════════════════════ */

  var _registry = {};
  var _observerInstalled = false;

  function registerVariant(variantId, tags, stockCap, price) {
    if (!variantId) return;
    var key = String(variantId);
    var existing = _registry[key];

    // Merge rule: prefer richer data. Don't overwrite a known stockCap with null,
    // and don't overwrite a longer tag list with a shorter one.
    var newTags = Array.isArray(tags) && tags.length ? tags : (existing ? existing.tags : []);
    var newStock = (typeof stockCap === 'number' && stockCap > 0)
      ? stockCap
      : (existing ? existing.stockCap : null);

    if (!existing || newTags.length >= existing.tags.length || newStock !== existing.stockCap) {
      _registry[key] = { tags: newTags, stockCap: newStock, price: existing ? existing.price : null };
    }

    // Price (cents): the latest known value wins. Never cleared by a caller
    // that doesn't pass one.
    var cents = _toCents(price);
    if (cents !== null) _registry[key].price = cents;
  }

  // Cents as a whole number, or null when the value is absent or unusable.
  function _toCents(price) {
    if (price === null || price === undefined || price === '') return null;
    var n = Number(price);
    return (isFinite(n) && n >= 0) ? Math.round(n) : null;
  }

  function getVariantInfo(variantId) {
    return _registry[String(variantId)] || null;
  }

  /* ═══════════════════════════════════════════════════════════════════════
     §2  LIMIT PARSING
     Handles:  limit-1  limit-one…limit-ten  limit:1
     Returns the lowest numeric limit found across all tags, or null.
     ═══════════════════════════════════════════════════════════════════════ */

  var WORD_TO_NUM = {
    one: 1, two: 2, three: 3, four: 4, five: 5,
    six: 6, seven: 7, eight: 8, nine: 9, ten: 10
  };

  function parseLimitFromTags(tags) {
    if (!Array.isArray(tags) || !tags.length) return null;
    // Prefer the shared parser (dcg-utils.js) — single source of truth.
    if (window.DCG && typeof window.DCG.parseLimit === 'function') {
      return window.DCG.parseLimit(tags);
    }
    var limits = [];
    for (var i = 0; i < tags.length; i++) {
      var tag = String(tags[i] || '').trim().toLowerCase();
      var m = tag.match(/^limit[-:](\d{1,3})$/);
      if (m) { var n = parseInt(m[1], 10); if (n > 0) limits.push(n); continue; }
      m = tag.match(/^limit-(one|two|three|four|five|six|seven|eight|nine|ten)$/);
      if (m) { var w = WORD_TO_NUM[m[1]]; if (w) limits.push(w); }
    }
    return limits.length ? Math.min.apply(null, limits) : null;
  }

  function parseLimitFromTagString(tagsAttr) {
    if (!tagsAttr) return null;
    return parseLimitFromTags(
      tagsAttr.split(',').map(function (t) { return t.trim(); }).filter(Boolean)
    );
  }

  /* ═══════════════════════════════════════════════════════════════════════
     §3  CART API HELPERS
     ═══════════════════════════════════════════════════════════════════════ */

  function fetchCart() {
    return fetch('/cart.js', {
      method: 'GET',
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json' }
    }).then(function (res) {
      if (!res.ok) throw new Error('[DCG enforcer] /cart.js ' + res.status);
      return res.json();
    });
  }

  function getCartQtyForVariant(cart, variantId) {
    var id = Number(variantId);
    var total = 0;
    if (!cart || !Array.isArray(cart.items)) return 0;
    for (var i = 0; i < cart.items.length; i++) {
      if (cart.items[i].variant_id === id) total += (cart.items[i].quantity || 0);
    }
    return total;
  }

  function updateCartVariantQty(variantId, newQty) {
    return fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      // v5 (v2-10-1): the id goes as text. Shopify answers 400 to an id sent
      // as a number ("expected Integer to be a ... : id"; seen on the draft,
      // 10 OCT 2026), so until now every correction of §5 and §6 failed and
      // was only written to the console. Text is accepted for a variant id
      // and for a line's key alike; the caller may pass either.
      body: JSON.stringify({ id: String(variantId), quantity: Number(newQty) })
    }).then(function (res) {
      if (!res.ok) throw new Error('[DCG enforcer] /cart/change.js ' + res.status);
      return res.json();
    });
  }

  // Removes one cart line. Keyed on the line's own key, so a second line of
  // the same variant (different properties) is left alone.
  function removeCartLine(item) {
    return fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: item.key ? String(item.key) : String(item.variant_id), quantity: 0 })
    }).then(function (res) {
      if (!res.ok) throw new Error('[DCG enforcer] /cart/change.js ' + res.status);
      return res.json();
    });
  }

  function _getJSON(url) {
    return fetch(url, {
      method: 'GET',
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json' }
    }).then(function (res) {
      if (!res.ok) throw new Error('[DCG enforcer] ' + url + ' ' + res.status);
      return res.json();
    });
  }

  /* ═══════════════════════════════════════════════════════════════════════
     §3b  PRICE PENDING (D-SP-14)
     A variant priced 0 cannot be ordered. Products tagged `event-ticket`
     are exempt. The rule is per variant: a product can have a priced
     Near Mint and $0.00 played conditions, and only the $0.00 ones stop.
     ═══════════════════════════════════════════════════════════════════════ */

  var PRICE_PENDING_MESSAGE = "Price pending - this item isn't available to order yet.";
  var PRICE_EXEMPT_TAG = 'event-ticket';

  function isPriceExempt(tags) {
    if (!Array.isArray(tags)) return false;
    for (var i = 0; i < tags.length; i++) {
      if (String(tags[i] || '').trim().toLowerCase() === PRICE_EXEMPT_TAG) return true;
    }
    return false;
  }

  // price in cents (number or numeric string). Unknown price → not pending.
  function isPricePending(price, tags) {
    return _toCents(price) === 0 && !isPriceExempt(tags);
  }

  function _handleFromUrl(url) {
    var m = String(url || '').match(/\/products\/([^\/?#]+)/);
    if (!m) return null;
    try { return decodeURIComponent(m[1]); } catch (e) { return m[1]; }
  }

  // Shopify's product answer (/products/<handle>.js): tags + variant prices.
  // handle → Promise<{ tags: string[], prices: { [variantId]: cents } }>.
  // Successes are kept for the page; a failed request is retried next time.
  var _productLookups = {};

  function lookupProduct(handle) {
    var key = String(handle);
    if (_productLookups[key]) return _productLookups[key];
    var pending = _getJSON('/products/' + encodeURIComponent(key) + '.js').then(function (product) {
      if (!product || !Array.isArray(product.tags)) throw new Error('[DCG enforcer] unusable product answer');
      var prices = {};
      (Array.isArray(product.variants) ? product.variants : []).forEach(function (v) {
        var cents = v ? _toCents(v.price) : null;
        if (v && v.id != null && cents !== null) prices[String(v.id)] = cents;
      });
      return { tags: product.tags, prices: prices };
    });
    _productLookups[key] = pending;
    pending.catch(function () { delete _productLookups[key]; });
    return pending;
  }

  // Shopify's price for a variant the registry has no price for.
  // Resolves to { price: cents, tags: string[]|null }, or null if Shopify
  // could not be asked.
  //   1. /variants/<id>.js gives the price from the variant id alone.
  //   2. Only when that price is 0 are the product's tags needed (event
  //      tickets are exempt): /variants/<id> redirects to the product page,
  //      whose handle is then asked through /products/<handle>.js.
  //      tags stays null if that second step fails.
  var _variantLookups = {};

  function lookupVariant(variantId) {
    var key = String(variantId);
    if (_variantLookups[key]) return _variantLookups[key];

    var pending = _getJSON('/variants/' + encodeURIComponent(key) + '.js').then(function (variant) {
      var price = _toCents(variant && variant.price);
      if (price === null) return null;
      if (price > 0) return { price: price, tags: null };

      return fetch('/variants/' + encodeURIComponent(key), {
        method: 'HEAD',
        credentials: 'same-origin',
        redirect: 'follow'
      }).then(function (res) {
        var handle = _handleFromUrl(res && res.url);
        if (!handle) throw new Error('[DCG enforcer] no product URL for variant ' + key);
        return lookupProduct(handle);
      }).then(function (product) {
        // The product answer is the newer of the two: its price wins.
        var fromProduct = product.prices[key];
        return { price: typeof fromProduct === 'number' ? fromProduct : price, tags: product.tags };
      }).catch(function (err) {
        console.warn('[DCG enforcer] tags lookup failed for $0.00 variant ' + key + ':', err);
        return { price: price, tags: null };
      });
    }).catch(function (err) {
      console.warn('[DCG enforcer] price lookup failed for variant ' + key + ':', err);
      return null;
    }).then(function (found) {
      // Keep only complete answers; anything else is asked again next time.
      if (!found || (found.price === 0 && !found.tags)) delete _variantLookups[key];
      return found;
    });

    _variantLookups[key] = pending;
    return pending;
  }

  // Promise<boolean>: is this variant price pending? Registry first, then
  // Shopify. Unknown after both → false (the add is allowed, as before v4;
  // the cart audit below is the backstop).
  function _checkPricePending(variantId) {
    var info = getVariantInfo(variantId);
    if (info && typeof info.price === 'number') {
      return Promise.resolve(isPricePending(info.price, info.tags));
    }
    return lookupVariant(variantId).then(function (found) {
      if (!found) return false;
      if (found.price > 0) {
        registerVariant(variantId, null, null, found.price);
        return false;
      }
      // $0.00 but the tags could not be read: a free event can't be ruled out.
      if (!found.tags) return false;
      registerVariant(variantId, found.tags, null, found.price);
      return isPricePending(found.price, found.tags);
    }).catch(function () { return false; });
  }

  /* ── $0.00 cart audit ──────────────────────────────────────────────────
     Removes every cart line whose ORIGINAL unit price is 0. `original_price`
     is the variant's price before any discount, so a line made free by a
     discount (original_price > 0, final_price 0) is never touched.

     /cart.js lines carry no product tags, so the event-ticket exemption is
     read from: the registry (cart page inputs carry data-tags, cards and the
     product page register theirs), else /products/<handle>.js for the line's
     `handle`. If the tags can't be read the line is LEFT IN PLACE: a free
     event ticket must never be removed.

     Resolves to the removed line items (possibly empty).
     ────────────────────────────────────────────────────────────────────── */
  function _tagsForCartItem(item) {
    var info = getVariantInfo(item.variant_id);
    if (info && info.tags.length) return Promise.resolve(info.tags);
    if (!item.handle) return Promise.resolve(null);
    return lookupProduct(item.handle)
      .then(function (product) { return product.tags; })
      .catch(function () { return null; });
  }

  function auditZeroPrice(cart) {
    var lines = (cart && Array.isArray(cart.items) ? cart.items : []).filter(function (item) {
      return item && typeof item.original_price === 'number' && item.original_price === 0;
    });
    if (!lines.length) return Promise.resolve([]);

    var removed = [];
    var chain = Promise.resolve();

    lines.forEach(function (item) {
      chain = chain.then(function () {
        return _tagsForCartItem(item);
      }).then(function (tags) {
        if (!tags) {
          console.warn('[DCG enforcer] $0.00 cart line left in place (product tags unavailable):', item.variant_id);
          return;
        }
        if (isPriceExempt(tags)) return;   // free event ticket
        return removeCartLine(item)
          .then(function () { removed.push(item); })
          .catch(function (err) {
            console.warn('[DCG enforcer] could not remove $0.00 line', item.variant_id, err);
          });
      });
    });

    return chain.then(function () { return removed; });
  }

  /* ──────────────────────────────────────────────────────────────────────
     §3c  IN-STORE ONLY  (v6, v2-10-2 — PROPOSED)
     A product tagged `retail` is sold in the store only. If one is in the
     cart it is taken out, the same way a $0.00 line is. v7: with no
     exception. An event ticket tagged `retail` is taken out like any
     other product (see the header).

     Where the tags come from: as for §3b, the registry (the cart page's
     inputs carry data-tags; cards and the product page register theirs),
     else /products/<handle>.js. If the tags can't be read the line is LEFT
     IN PLACE.

     Cost. §3b asks only about $0.00 lines, which are rare. This rule has to
     know the tags of every line. On the cart page they are in the page. On
     other pages a product the page does not show costs one request; so the
     answer "not in-store only" is remembered for the browser session for
     15 minutes (sessionStorage), and a cart of twenty products costs twenty
     requests once, not on every page view. "In-store only" is never taken
     from memory: a line is only removed on what Shopify says now.
     ────────────────────────────────────────────────────────────────────── */
  var IN_STORE_TAG = 'retail';
  var IN_STORE_WHY = 'available in store only';   // v7: the words §7 knows the message by
  var IN_STORE_KEY = 'dcgEnforcerNotInStoreOnly';
  var IN_STORE_TTL = 15 * 60 * 1000;

  function isInStoreOnly(tags) {
    if (!Array.isArray(tags)) return false;   // v7: no exception for event tickets
    for (var i = 0; i < tags.length; i++) {
      if (String(tags[i] || '').trim().toLowerCase() === IN_STORE_TAG) return true;
    }
    return false;
  }

  function _knownNotInStoreOnly(handle) {
    try {
      var map = JSON.parse(window.sessionStorage.getItem(IN_STORE_KEY) || '{}');
      return typeof map[handle] === 'number' && map[handle] > Date.now();
    } catch (e) { return false; }
  }

  function _rememberNotInStoreOnly(handle) {
    try {
      var map = JSON.parse(window.sessionStorage.getItem(IN_STORE_KEY) || '{}');
      var now = Date.now();
      Object.keys(map).forEach(function (h) { if (!(map[h] > now)) delete map[h]; });
      map[handle] = now + IN_STORE_TTL;
      window.sessionStorage.setItem(IN_STORE_KEY, JSON.stringify(map));
    } catch (e) {}
  }

  // Resolves to true (in-store only), false (not), or null (could not be read).
  function _inStoreOnlyForCartItem(item) {
    var info = getVariantInfo(item.variant_id);
    if (info && info.tags.length) return Promise.resolve(isInStoreOnly(info.tags));
    if (!item.handle) return Promise.resolve(null);
    if (_knownNotInStoreOnly(item.handle)) return Promise.resolve(false);
    return lookupProduct(item.handle)
      .then(function (product) {
        var yes = isInStoreOnly(product.tags);
        if (!yes) _rememberNotInStoreOnly(item.handle);
        return yes;
      })
      .catch(function () { return null; });
  }

  // Resolves to the removed line items (possibly empty), each marked
  // _dcgWhy = 'instore'.
  function auditInStoreOnly(cart) {
    var lines = (cart && Array.isArray(cart.items) ? cart.items : []).filter(function (item) { return !!item; });
    if (!lines.length) return Promise.resolve([]);

    var removed = [];
    var chain = Promise.resolve();

    lines.forEach(function (item) {
      chain = chain.then(function () {
        return _inStoreOnlyForCartItem(item);
      }).then(function (yes) {
        if (yes !== true) return;
        return removeCartLine(item)
          .then(function () { item._dcgWhy = 'instore'; removed.push(item); })
          .catch(function (err) {
            console.warn('[DCG enforcer] could not remove in-store-only line', item.variant_id, err);
          });
      });
    });

    return chain.then(function () { return removed; });
  }

  // Both removal rules, one after the other: §3b ($0.00 lines), then §3c
  // (in-store only) on what is left. Resolves to every removed line.
  function auditRemovals(cart) {
    return auditZeroPrice(cart).then(function (gone) {
      var rest = (cart && Array.isArray(cart.items) ? cart.items : []).filter(function (item) { return gone.indexOf(item) === -1; });
      return auditInStoreOnly({ items: rest }).then(function (more) { return gone.concat(more); });
    });
  }

  // One sentence per reason. With only price-pending lines the words are
  // those of v4.
  function _removedMessage(removed) {
    var name = function (item) { return '\u201c' + (item.title || item.product_title || 'An item') + '\u201d'; };
    var sentence = function (items, why) {
      return items.map(name).join(', ') + (items.length === 1 ? ' was' : ' were') + ' removed from your cart (' + why + ').';
    };
    var pending = removed.filter(function (item) { return item._dcgWhy !== 'instore'; });
    var inStore = removed.filter(function (item) { return item._dcgWhy === 'instore'; });
    var parts = [];
    if (pending.length) parts.push(sentence(pending, 'price pending - not available to order yet'));
    if (inStore.length) parts.push(sentence(inStore, IN_STORE_WHY));
    return parts.join(' ');
  }

  // A cart-page removal is followed by a reload (the page's rows, remove
  // links and totals are server-rendered per line); the message is carried
  // across it and shown in the banner.
  var NOTICE_KEY = 'dcgEnforcerNotice';
  var NOTICE_PARAM = 'dcg_notice';
  var _carriedNoticeShown = false;

  function _showCarriedNotice() {
    if (!document.querySelector('.template-cart')) return;
    var message = null;
    try {
      message = window.sessionStorage.getItem(NOTICE_KEY);
      if (message) window.sessionStorage.removeItem(NOTICE_KEY);
    } catch (e) {}
    if (!message && /[?&]dcg_notice=price-pending(&|$)/.test(window.location.search)) {
      message = 'An item was removed from your cart (price pending - not available to order yet).';
    }
    if (!message && /[?&]dcg_notice=in-store-only(&|$)/.test(window.location.search)) {   // v6
      message = 'An item was removed from your cart (' + IN_STORE_WHY + ').';
    }
    if (!message) return;
    _carriedNoticeShown = true;
    showCartBanner(message);
  }

  // Header cart (count + dropdown), refreshed the way dcg-cart-bridge.js does
  // after an add (:62-71): the theme's own adjustCartDropDown, then
  // cart:updated. That event also brings this file's audit round again half a
  // second later, which finds nothing to remove and checks the count once
  // more (_syncHeaderCount), so a slower, older answer cannot leave it wrong.
  function refreshCartUI() {
    try {
      if (typeof window.adjustCartDropDown === 'function') {
        window.adjustCartDropDown();
      } else {
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      }
    } catch (err) {
      console.warn('[DCG enforcer] cart refresh failed:', err);
    }
    try { document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true })); } catch (e) {}
  }

  // The header count (.cart-qty, written by shop.js adjustCartDropDown from
  // its own /cart.js request) against a cart this file has just read. The
  // theme asks for the cart at page load; if this file removes a line while
  // that request is out, the older answer can arrive last and leave the old
  // count showing. A count that is still blank is left for the theme to fill.
  function _syncHeaderCount(cart) {
    if (!cart || typeof cart.item_count !== 'number') return;
    if (typeof window.adjustCartDropDown !== 'function') return;
    var badges = document.querySelectorAll('.cart-qty');
    for (var i = 0; i < badges.length; i++) {
      var shown = (badges[i].textContent || '').trim();
      if (shown !== '' && shown !== String(cart.item_count)) {
        window.adjustCartDropDown();
        return;
      }
    }
  }

  // Returns true when the page is being reloaded.
  function _announceRemoved(removed) {
    var message = _removedMessage(removed);

    if (document.querySelector('.template-cart')) {
      // One reload per page view: if this view already shows a carried
      // notice, say it in place rather than reload again.
      if (_carriedNoticeShown) {
        showCartBanner(message);
        refreshCartUI();
        _announceCorrected();   // v5: the rows still show the line that has gone
        return false;
      }
      var stored = false;
      try { window.sessionStorage.setItem(NOTICE_KEY, message); stored = true; } catch (e) {}
      if (stored) {
        window.location.reload();
      } else {
        // v6: the words the fallback notice carries follow the reason
        var allInStore = removed.every(function (item) { return item._dcgWhy === 'instore'; });
        window.location.replace(window.location.pathname + '?' + NOTICE_PARAM + '=' + (allInStore ? 'in-store-only' : 'price-pending'));
      }
      return true;
    }

    showToast('\u26a0\ufe0f Cart updated: ' + message);
    refreshCartUI();
    return false;
  }

  // Page load, every page: $0.00 lines out first, then (cart page only) the
  // quantity audit in §5.
  //
  // v5 (v2-10-1): the same function is the public auditCart(), which the cart
  // page runs after every quantity change it makes in place
  // (assets/dcg-cart-page.js). So that the caller can wait for it, it now
  // returns a Promise of { reloading, removed, corrected }: whether the page
  // is being reloaded, how many $0.00 lines went, how many quantities §5
  // put right. What it does is unchanged.
  function auditOnLoad() {
    var onCartPage = !!document.querySelector('.template-cart');
    var loaded = null;
    var result = { reloading: false, removed: 0, corrected: 0 };
    var quantities = function () {
      if (!onCartPage) return result;
      return auditCartPage().then(function (n) { result.corrected = n; return result; });
    };
    return fetchCart().then(function (cart) {
      loaded = cart;
      return auditRemovals(cart);       // v6: §3b, then §3c
    }).then(function (removed) {
      result.removed = removed.length;
      if (removed.length && _announceRemoved(removed)) { result.reloading = true; return result; }
      if (!removed.length) _syncHeaderCount(loaded);
      return quantities();
    }).catch(function (err) {
      console.warn('[DCG enforcer] price-pending cart audit failed:', err);
      return quantities();
    });
  }

  // v5: the cart page's rows and totals are drawn by the server. After this
  // file has changed the cart from the cart page without a reload, it says so,
  // and assets/dcg-cart-page.js draws the rows and the subtotal again.
  function _announceCorrected() {
    try { document.dispatchEvent(new CustomEvent('dcg:cart:corrected', { bubbles: true })); } catch (e) {}
  }

  /* ═══════════════════════════════════════════════════════════════════════
     §4  ATC PRE-FLIGHT
     Called by dcg-cart-bridge.js BEFORE every /cart/add.js request.
     Returns a Promise resolving to: { qty, blocked, message }

     Price pending (§3b) is checked first: a $0.00 variant is blocked before
     any quantity is considered.

     Enforcement sources (in priority order):
       1. Registry (pre-populated at DOMContentLoaded + observer)
       2. Live DOM scan (catches cards missed by observer timing)
     effectiveMax = min(limitTag, stockCap)
     If neither is known, the variant passes through safely (fail open).
     The cart page audit (§5) is always the guaranteed final backstop.
     ═══════════════════════════════════════════════════════════════════════ */

  function preflight(variantId, requestedQty) {
    var vid = Number(variantId);
    var qty = Number(requestedQty) || 1;

    if (!vid || qty <= 0) {
      return Promise.resolve({ qty: qty, blocked: false, message: null });
    }

    // ── Step 1: registry lookup ────────────────────────────────────────────
    var info = getVariantInfo(vid);

    // ── Step 2: live DOM scan if registry missed ───────────────────────────
    // After Phase 2 (enrichCard) completes, data-product-tags,
    // data-product-variants, and data-variant-inventory are all on the wrapper.
    // The observer walk-up should have caught this, but we scan as a fallback
    // to handle any timing gaps between Phase 2 completion and observer firing.
    if (!info) {
      var scanned = _scanDOMForVariant(vid);
      if (scanned) {
        registerVariant(vid, scanned.tags, scanned.stockCap, scanned.price);
        info = getVariantInfo(vid);
      }
    }

    // ── Step 2b: price pending ────────────────────────────────────────────
    return _checkPricePending(vid).then(function (pending) {
      if (pending) {
        return { qty: 0, blocked: true, message: PRICE_PENDING_MESSAGE };
      }
      return _preflightQuantity(vid, qty);
    });
  }

  function _preflightQuantity(vid, qty) {
    var info = getVariantInfo(vid);

    // ── Step 3: compute effective max ─────────────────────────────────────
    var limit    = info ? parseLimitFromTags(info.tags) : null;
    var stockCap = info ? info.stockCap : null;

    var effectiveMax = Math.min(
      limit    !== null ? limit    : Infinity,
      stockCap !== null ? stockCap : Infinity
    );

    // No enforceable constraint found — pass through immediately (no fetch).
    // Cart page audit will still correct any overage before checkout.
    if (!isFinite(effectiveMax)) {
      return Promise.resolve({ qty: qty, blocked: false, message: null });
    }

    // ── Step 4: cross-check current cart ──────────────────────────────────
    return fetchCart().then(function (cart) {
      var inCart = getCartQtyForVariant(cart, vid);

      // Build a clear reason string for user messages
      var reasonFull  = _buildReason(limit, stockCap, effectiveMax, 'full');
      var reasonShort = _buildReason(limit, stockCap, effectiveMax, 'short');

      if (inCart >= effectiveMax) {
        return {
          qty: 0,
          blocked: true,
          message: 'You already have ' + inCart + ' of this item in your cart ('
            + reasonFull + ').'
        };
      }

      var allowable = effectiveMax - inCart;

      if (qty > allowable) {
        return {
          qty: allowable,
          blocked: false,
          message: 'Quantity reduced to ' + allowable + ' \u2014 ' + reasonShort + '.'
        };
      }

      return { qty: qty, blocked: false, message: null };

    }).catch(function (err) {
      console.warn('[DCG enforcer] preflight cart fetch failed \u2014 allowing add:', err);
      return { qty: qty, blocked: false, message: null };
    });
  }

  // Produce a human-readable constraint reason for toast messages.
  // mode='full'  → "limit: 6 per customer" / "only 6 available" / "max: 6"
  // mode='short' → "limit is 6 per customer" / "6 remaining in stock"
  function _buildReason(limit, stockCap, effectiveMax, mode) {
    var byLimit = limit !== null && (stockCap === null || limit <= stockCap);
    if (byLimit) {
      return mode === 'full'
        ? 'limit: ' + effectiveMax + ' per customer'
        : 'limit is ' + effectiveMax + ' per customer';
    }
    if (stockCap !== null && (limit === null || stockCap < limit)) {
      return mode === 'full'
        ? 'only ' + effectiveMax + ' available'
        : effectiveMax + ' remaining in stock';
    }
    return mode === 'full'
      ? 'max: ' + effectiveMax
      : 'max is ' + effectiveMax;
  }

  /* ── Live DOM scan ─────────────────────────────────────────────────────
     Searches the current document for a card wrapper or product section
     containing this variantId. Returns { tags, stockCap, price } or null.

     Called by preflight() when the variant is absent from the registry —
     this catches any cards that Phase 2 enriched before the observer walked
     up, or cases where the user clicked ATC in the gap between Phase 2 and
     the observer callback firing.
     ────────────────────────────────────────────────────────────────────── */
  function _scanDOMForVariant(variantId) {
    var vid = String(variantId);

    // Source A: product page section (data-dcg-tags + ProductSelect)
    var sections = document.querySelectorAll(
      '[data-section-type="product"], [data-section-type="product-template"]'
    );
    for (var s = 0; s < sections.length; s++) {
      var masterSelect = sections[s].querySelector('select[id^="ProductSelect-"]');
      if (!masterSelect) continue;
      var opts = masterSelect.options;
      for (var o = 0; o < opts.length; o++) {
        if (String(opts[o].value) === vid) {
          var tagsAttr = sections[s].getAttribute('data-dcg-tags') || '';
          var tags = tagsAttr.split(',').map(function (t) { return t.trim(); }).filter(Boolean);
          // Product-page stock: read the SAME data-variant-inventory map the
          // collection cards emit, and apply the IDENTICAL cap logic used in
          // Source B below — Shopify-managed + deny policy + qty > 0 → that qty
          // is the stock cap. This makes cumulative add-to-cart on the product
          // page enforce stock exactly like the collection page (dcg-qty-guard
          // only caps the qty INPUT, not repeated single-qty ATC clicks). A
          // 'continue' policy or qty 0 (untracked / BinderPOS-unsynced) stays
          // uncapped — same as the cards.
          var pdpStockCap = null;
          var pdpPrice = null;
          var invAttr = sections[s].getAttribute('data-variant-inventory');
          if (invAttr) {
            // Preferred: the SAME per-variant map the collection cards emit,
            // with identical logic (Source B) — Shopify-managed + deny + qty>0.
            // A 'continue' policy or qty 0 stays uncapped, matching the cards.
            try {
              var invMap = JSON.parse(invAttr);
              var inv = invMap[vid] || invMap[Number(vid)];
              if (inv && inv.management === 'shopify' && inv.policy === 'deny' && inv.qty > 0) {
                pdpStockCap = inv.qty;
              }
              // v4: the same map carries each variant's price (cents).
              if (inv) pdpPrice = _toCents(inv.price);
            } catch (e) {}
          } else {
            // Fallback when the section has no inventory map (older template):
            // the master <select> option carries data-stock (Shopify
            // inventory_quantity), already present on every product page.
            // Positive → cap at on-hand stock.
            var ds = parseInt(opts[o].getAttribute('data-stock'), 10);
            if (isFinite(ds) && ds > 0) pdpStockCap = ds;
          }
          if (tags.length || pdpStockCap !== null || pdpPrice !== null) {
            return { tags: tags, stockCap: pdpStockCap, price: pdpPrice };
          }
          return null;
        }
      }
    }

    // Source B: product card wrappers (data-product-tags, set by Phase 2 enrichCard)
    // Note: data-product-tags requires Phase 2 to have completed. The observer
    // walk-up handles most cases; this scan is the timing-gap fallback.
    var cards = document.querySelectorAll('[data-product-tags]');
    for (var c = 0; c < cards.length; c++) {
      var card = cards[c];
      try {
        // Check if this card contains our variant
        var variantsAttr = card.getAttribute('data-product-variants');
        if (!variantsAttr) continue;

        var variants = JSON.parse(variantsAttr);
        if (!Array.isArray(variants)) continue;

        var matched = null;
        for (var mv = 0; mv < variants.length; mv++) {
          if (variants[mv] && String(variants[mv].id) === vid) { matched = variants[mv]; break; }
        }
        if (!matched) continue;

        // Extract tags
        var cardTags = JSON.parse(card.getAttribute('data-product-tags') || '[]');

        // Extract stock cap from data-variant-inventory (set by enrichCard)
        var stockCap = null;
        var invAttr = card.getAttribute('data-variant-inventory');
        if (invAttr) {
          var invMap = JSON.parse(invAttr);
          // Keys in JSON-serialized object are strings
          var inv = invMap[vid] || invMap[Number(vid)];
          if (inv && inv.management === 'shopify' && inv.policy === 'deny' && inv.qty > 0) {
            stockCap = inv.qty;
          }
        }

        return {
          tags:     Array.isArray(cardTags) ? cardTags : [],
          stockCap: stockCap,
          price:    _toCents(matched.price)   // cents, as Liquid / product .js give it
        };

      } catch (e) {
        // Malformed JSON on this card — skip, try others
      }
    }

    return null;
  }

  /* ═══════════════════════════════════════════════════════════════════════
     §5  CART PAGE ON-LOAD AUDIT
     Runs once when .template-cart is present. Reads each qty input's
     data-tags and max attributes, computes effective max, and corrects
     any qty via /cart/change.js. Shows a persistent banner.

     This is the guaranteed enforcement backstop — it runs regardless of
     whether the ATC preflight caught the violation, and it handles cases
     where the customer navigates directly to /cart with an over-limit cart.
     ═══════════════════════════════════════════════════════════════════════ */

  // v5: returns a Promise of the number of quantities put right (0 when none).
  function auditCartPage() {
    if (!document.querySelector('.template-cart')) return Promise.resolve(0);

    var inputs = document.querySelectorAll(
      '.template-cart input.cart__qty-input[data-variant-id]'
    );
    if (!inputs.length) return Promise.resolve(0);

    var corrections = [];

    for (var i = 0; i < inputs.length; i++) {
      var inp = inputs[i];
      var variantId = inp.getAttribute('data-variant-id');
      var currentVal = parseInt(inp.value, 10);

      if (!variantId || isNaN(currentVal) || currentVal <= 0) continue;

      // Source A: limit-N product tags (always in data-tags, set by Liquid)
      var limit = parseLimitFromTagString(inp.getAttribute('data-tags') || '');

      // Source B: Shopify stock cap (max attr — set only for Shopify-managed
      // inventory with deny policy; blank for BinderPOS-managed products)
      var stockCap = Infinity;
      var maxAttr = inp.getAttribute('max');
      if (maxAttr) {
        var parsedMax = parseInt(maxAttr, 10);
        if (!isNaN(parsedMax) && parsedMax > 0) stockCap = parsedMax;
      }

      var effectiveMax = Math.min(
        limit !== null ? limit : Infinity,
        stockCap
      );

      if (!isFinite(effectiveMax) || effectiveMax <= 0) continue;
      if (currentVal <= effectiveMax) continue;

      corrections.push({
        inp:         inp,
        variantId:   variantId,
        correctTo:   effectiveMax,
        limit:       limit,
        stockCap:    isFinite(stockCap) ? stockCap : null,
        originalVal: currentVal
      });
    }

    if (!corrections.length) return Promise.resolve(0);

    var messages = [];
    var chain = Promise.resolve();

    corrections.forEach(function (c) {
      chain = chain.then(function () {
        return updateCartVariantQty(c.variantId, c.correctTo)
          .then(function () {
            c.inp.value = String(c.correctTo);

            var stepper = _closestByClass(c.inp, 'cart-qty-btns');
            if (stepper) {
              var plus = stepper.querySelector('.inc, .qtyplus');
              if (plus) {
                var atMax = c.correctTo >= c.correctTo; // always true at effectiveMax
                plus.classList.add('qty-at-max');
                plus.setAttribute('aria-disabled', 'true');
              }
            }

            if (c.correctTo === 0) {
              messages.push('An item was removed from your cart (purchase limit exceeded).');
            } else if (c.stockCap !== null && (c.limit === null || c.stockCap <= c.limit)) {
              messages.push(
                'Quantity adjusted to ' + c.correctTo
                + ' (only ' + c.stockCap + ' available).'
              );
            } else {
              messages.push(
                'Quantity adjusted to ' + c.correctTo
                + ' (limit: ' + c.limit + ' per customer).'
              );
            }
          })
          .catch(function (err) {
            console.warn('[DCG enforcer] cart/change.js failed for variant', c.variantId, err);
          });
      });
    });

    return chain.then(function () {
      if (!messages.length) return 0;
      showCartBanner(messages.join(' '));
      if (typeof window.adjustCartDropDown === 'function') {
        window.adjustCartDropDown();
      } else {
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      }
      // v5: the line totals and the subtotal on the page are still those of
      // the quantities before the correction.
      _announceCorrected();
      return messages.length;
    });
  }

  /* ═══════════════════════════════════════════════════════════════════════
     §6  CART DRAWER AUDIT (best-effort)
     ═══════════════════════════════════════════════════════════════════════ */

  var _drawerAuditPending = false;

  function scheduleDrawerAudit() {
    if (_drawerAuditPending) return;
    _drawerAuditPending = true;
    setTimeout(function () {
      _drawerAuditPending = false;
      runDrawerAudit();
    }, 500);
  }

  function runDrawerAudit() {
    fetchCart().then(function (cart) {
      if (!cart || !Array.isArray(cart.items)) return;
      if (!cart.items.length) { _syncHeaderCount(cart); return; }

      // v4: $0.00 lines out first (§3b), whatever the registry holds.
      return auditRemovals(cart).then(function (removed) {       // v6: §3b, then §3c
        if (!removed.length) _syncHeaderCount(cart);
        if (removed.length) {
          if (_announceRemoved(removed)) return;   // cart page: reloading
          cart.items = cart.items.filter(function (item) { return removed.indexOf(item) === -1; });
        }
        _auditDrawerQuantities(cart, removed.length ? _removedMessage(removed) : '');
      });
    }).catch(function (err) {
      console.warn('[DCG enforcer] drawer audit /cart.js fetch failed:', err);
    });
  }

  // `alreadySaid`: the price-pending removal message this audit has just put
  // in the toast, repeated in front of any quantity message so one toast
  // doesn't replace the other.
  function _auditDrawerQuantities(cart, alreadySaid) {
    // Only run if at least one variant in the registry has an enforceable constraint
    var hasConstraints = Object.keys(_registry).some(function (vid) {
      var info = _registry[vid];
      return parseLimitFromTags(info.tags) !== null || info.stockCap !== null;
    });
    if (!hasConstraints) return;

    {
      if (!cart || !Array.isArray(cart.items) || !cart.items.length) return;

      var violations = [];
      var seenVariantIds = {};

      cart.items.forEach(function (item) {
        var vid = item.variant_id;
        if (seenVariantIds[vid]) return;

        var info = getVariantInfo(vid);
        if (!info) return;

        var limit    = parseLimitFromTags(info.tags);
        var stockCap = info.stockCap;
        var effectiveMax = Math.min(
          limit    !== null ? limit    : Infinity,
          stockCap !== null ? stockCap : Infinity
        );

        if (!isFinite(effectiveMax)) return;

        var totalQty = getCartQtyForVariant(cart, vid);
        if (totalQty <= effectiveMax) return;

        seenVariantIds[vid] = true;
        violations.push({
          variantId:    vid,
          title:        item.title,
          correctTo:    effectiveMax,
          limit:        limit,
          stockCap:     stockCap
        });
      });

      if (!violations.length) return;

      var messages = [];
      var chain = Promise.resolve();

      violations.forEach(function (v) {
        chain = chain.then(function () {
          return updateCartVariantQty(v.variantId, v.correctTo)
            .then(function () {
              var reason = v.stockCap !== null && (v.limit === null || v.stockCap <= v.limit)
                ? 'only ' + v.stockCap + ' available'
                : 'limit: ' + v.limit + ' per customer';
              messages.push(
                '\u201c' + v.title + '\u201d adjusted to ' + v.correctTo
                + ' (' + reason + ').'
              );
            })
            .catch(function (err) {
              console.warn('[DCG enforcer] drawer audit correction failed:', err);
            });
        });
      });

      chain.then(function () {
        if (!messages.length) return;
        if (alreadySaid) messages.unshift(alreadySaid);
        showToast('\u26a0\ufe0f Cart updated: ' + messages.join(' '));
        if (typeof window.adjustCartDropDown === 'function') {
          window.adjustCartDropDown();
        }
      });
    }
  }

  /* ═══════════════════════════════════════════════════════════════════════
     §7  MESSAGE UI
     ═══════════════════════════════════════════════════════════════════════ */

  // v7: an in-store-only message gives a next step. The words are put in as
  // text (a product's name is never read as markup); the link is built here.
  // Returns true when the link was added.
  var CONTACT_URL = '/pages/contact';   // where the theme's own "In-store only" notices send "Contact us"
  var CONTACT_WORDS = 'Contact us';

  function _setMessage(el, message) {
    el.textContent = message;
    if (String(message).indexOf(IN_STORE_WHY) === -1) return false;
    var link = document.createElement('a');
    link.href = CONTACT_URL;
    link.textContent = CONTACT_WORDS;
    link.setAttribute('data-dcg-enforcer-contact', '');
    // pointer-events: the message of other pages lets presses through; its link takes them
    link.style.cssText = 'color:inherit;font-weight:700;text-decoration:underline;white-space:nowrap;pointer-events:auto;cursor:pointer';
    el.appendChild(document.createTextNode(' '));
    el.appendChild(link);
    return true;
  }

  function showToast(message) {
    var toast = document.getElementById('dcg-enforcer-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'dcg-enforcer-toast';
      toast.setAttribute('role', 'alert');
      toast.setAttribute('aria-live', 'assertive');
      toast.style.cssText = [
        'position:fixed',
        'top:20px',
        'left:50%',
        'transform:translateX(-50%)',
        'z-index:10000',
        'background:var(--dcg-orange,#FF7A00)',
        'color:#fff',
        'padding:11px 22px',
        'border-radius:6px',
        'font-size:13px',
        'font-weight:600',
        'line-height:1.45',
        'box-shadow:0 4px 20px rgba(0,0,0,.3)',
        'max-width:90vw',
        'width:420px',
        'text-align:center',
        'pointer-events:none',
        'transition:opacity .3s ease',
        'display:none',
        'opacity:0'
      ].join(';');
      document.body.appendChild(toast);
      // v7: a message is not taken away while the pointer or the keyboard is on its link
      var hold = function () { toast._dcgHeld = true; clearTimeout(toast._dcgTimer); };
      var release = function () { if (!toast._dcgHeld) return; toast._dcgHeld = false; if (toast._dcgHide) toast._dcgTimer = setTimeout(toast._dcgHide, 2000); };
      toast.addEventListener('mouseenter', hold);
      toast.addEventListener('mouseleave', release);
      toast.addEventListener('focusin', hold);
      toast.addEventListener('focusout', release);
    }
    var hasLink = _setMessage(toast, message);                    // v7 (the link takes presses; the message does not)
    toast._dcgHeld = false;                                       // a new message starts its own time
    toast.style.display = 'block';
    void toast.offsetHeight;
    toast.style.opacity = '1';
    clearTimeout(toast._dcgTimer);
    toast._dcgHide = function () {
      toast.style.opacity = '0';
      setTimeout(function () { if (toast.parentNode && toast.style.opacity === '0') toast.style.display = 'none'; }, 320);
    };
    toast._dcgTimer = setTimeout(toast._dcgHide, hasLink ? 8000 : 5000);
  }

  function showCartBanner(message) {
    var existing = document.getElementById('dcg-enforcer-banner');
    if (existing && existing.parentNode) existing.parentNode.removeChild(existing);

    var banner = document.createElement('div');
    banner.id = 'dcg-enforcer-banner';
    banner.setAttribute('role', 'alert');
    banner.setAttribute('aria-live', 'polite');
    banner.style.cssText = [
      'background:var(--dcg-orange,#FF7A00)',
      'color:#fff',
      'padding:13px 20px',
      'border-radius:6px',
      'font-size:13px',
      'font-weight:600',
      'line-height:1.5',
      'margin-bottom:18px',
      'text-align:center'
    ].join(';');
    _setMessage(banner, '\u26a0\ufe0f ' + message);   // v7: an in-store-only message ends with "Contact us"

    // v8: in the cart section, above the rows; or above the empty state when
    // the cart has no line left (`.template-cart` is the <body>: the first
    // child of that is not a place a customer sees).
    var form = document.querySelector('.template-cart form[action="/cart"]');
    var emptyState = document.querySelector('.template-cart [data-dcg-cart-section] .empty-page-content') ||
                     document.querySelector('.template-cart .empty-page-content');
    var section = document.querySelector('.template-cart [data-dcg-cart-section]');
    if (form && form.parentNode) {
      form.parentNode.insertBefore(banner, form);
    } else if (emptyState && emptyState.parentNode) {
      banner.setAttribute('data-dcg-enforcer-banner-empty', '');
      emptyState.parentNode.insertBefore(banner, emptyState);
    } else if (section) {
      section.insertBefore(banner, section.firstChild);
    } else {
      var cartEl = document.querySelector('.template-cart');
      if (cartEl) cartEl.insertBefore(banner, cartEl.firstChild);
    }
  }

  /* ═══════════════════════════════════════════════════════════════════════
     REGISTRY BUILDER + MUTATIONOBSERVER
     ═══════════════════════════════════════════════════════════════════════ */

  function buildRegistry() {
    // Source 1: product page sections
    var sections = document.querySelectorAll(
      '[data-section-type="product"], [data-section-type="product-template"]'
    );
    for (var s = 0; s < sections.length; s++) {
      var tagsAttr = sections[s].getAttribute('data-dcg-tags') || '';
      var tags = tagsAttr.split(',').map(function (t) { return t.trim(); }).filter(Boolean);
      var ms = sections[s].querySelector('select[id^="ProductSelect-"]');
      if (!ms) continue;
      // Register product-page variants WITH their stock cap — read it the SAME
      // way the collection cards do (_registerCardEl): prefer the
      // data-variant-inventory map (Shopify-managed + deny policy + qty>0), else
      // fall back to each option's data-stock (Shopify inventory_quantity, which
      // is already present on every product page). These were previously
      // registered with a NULL cap, which shadowed every stock lookup and let
      // add-to-cart exceed on-hand stock on the product page.
      var secInvMap = {};
      var secInvAttr = sections[s].getAttribute('data-variant-inventory');
      if (secInvAttr) { try { secInvMap = JSON.parse(secInvAttr); } catch (e) { secInvMap = {}; } }
      for (var o = 0; o < ms.options.length; o++) {
        var optVid = ms.options[o].value;
        if (!optVid) continue;
        var optStock = null;
        var invEntry = secInvMap[optVid] || secInvMap[Number(optVid)];
        var optPrice = invEntry ? _toCents(invEntry.price) : null;   // v4: cents, same map
        if (invEntry && invEntry.management === 'shopify' && invEntry.policy === 'deny' && invEntry.qty > 0) {
          optStock = invEntry.qty;
        } else {
          var ods = parseInt(ms.options[o].getAttribute('data-stock'), 10);
          if (isFinite(ods) && ods > 0) optStock = ods;
        }
        registerVariant(optVid, tags, optStock, optPrice);
      }
    }

    // Source 2: product card wrappers (set by Phase 2 enrichCard)
    var cards = document.querySelectorAll('[data-product-tags][data-product-variants]');
    for (var c = 0; c < cards.length; c++) {
      _registerCardEl(cards[c]);
    }

    // Source 3: cart page inputs
    var cartInputs = document.querySelectorAll('input[data-variant-id][data-tags]');
    for (var i = 0; i < cartInputs.length; i++) {
      var vid = cartInputs[i].getAttribute('data-variant-id');
      var inputTags = (cartInputs[i].getAttribute('data-tags') || '')
        .split(',').map(function (t) { return t.trim(); }).filter(Boolean);
      // Stock from max attr (Shopify-managed only)
      var inputStock = null;
      var ma = cartInputs[i].getAttribute('max');
      if (ma) { var mp = parseInt(ma, 10); if (!isNaN(mp) && mp > 0) inputStock = mp; }
      if (vid) registerVariant(vid, inputTags, inputStock);
    }

    _installObserver();
  }

  // Register all variants from a single card element.
  // Reads data-product-tags (tags), data-product-variants (variant IDs and,
  // since v4, each variant's price in cents), and data-variant-inventory
  // (stock counts — set by Phase 2 enrichCard).
  function _registerCardEl(el) {
    if (!el) return;
    try {
      var cardTags = JSON.parse(el.getAttribute('data-product-tags') || '[]');
      var variants = JSON.parse(el.getAttribute('data-product-variants') || '[]');
      var invMap   = {};
      var invAttr  = el.getAttribute('data-variant-inventory');
      if (invAttr) invMap = JSON.parse(invAttr);

      if (!Array.isArray(variants)) return;

      variants.forEach(function (v) {
        if (!v || !v.id) return;
        var vid = String(v.id);

        // Extract stock cap: only for Shopify-managed inventory with deny policy
        var stockCap = null;
        var inv = invMap[vid] || invMap[Number(vid)];
        if (inv && inv.management === 'shopify' && inv.policy === 'deny' && inv.qty > 0) {
          stockCap = inv.qty;
        }

        registerVariant(v.id, Array.isArray(cardTags) ? cardTags : [], stockCap, v.price);
      });
    } catch (e) {
      // Malformed JSON — skip silently
    }
  }

  // Install MutationObservers ONLY on the containers where product cards
  // appear or get hydrated. Previously this observed document.body with
  // subtree:true — every DOM mutation anywhere on the page fired the
  // handler, which on heavy collection pages (Flesh-and-Blood, MTG Singles)
  // caused tens of thousands of unnecessary walk-up traversals per card
  // render burst, pinning the main thread for minutes. Scoped observers
  // fire only when the watched container's subtree mutates — typically
  // 100× fewer callbacks on heavy pages.
  //
  // The observer callback walks UP from each addedNode to find the card
  // wrapper. This catches Phase 2 (enrichCard) completions on advanced-search:
  //   1. Phase 2 sets data-product-tags on the wrapper (attribute change —
  //      NOT caught by childList observer).
  //   2. Phase 2 calls dcgInitProductCards → buildCardUI → modifies card
  //      innerHTML (childList change — IS caught).
  //   3. The addedNodes from step 2 are inner divs (.dcg-card-top etc.).
  //      Walking up 6 levels reaches the wrapper, which now has data-product-tags.
  //   4. _registerCardEl registers all variants from that wrapper.
  function _installObserver() {
    if (_observerInstalled || typeof MutationObserver === 'undefined') return;
    _observerInstalled = true;

    var observerCallback = function (mutations) {
      for (var m = 0; m < mutations.length; m++) {
        var added = mutations[m].addedNodes;
        for (var n = 0; n < added.length; n++) {
          var node = added[n];
          if (node.nodeType !== 1) continue;

          // The added node itself is a card wrapper
          if (node.hasAttribute &&
              node.hasAttribute('data-product-tags') &&
              node.hasAttribute('data-product-variants')) {
            _registerCardEl(node);
          }

          // A container holding multiple card wrappers
          if (node.querySelectorAll) {
            var inner = node.querySelectorAll('[data-product-tags][data-product-variants]');
            for (var ic = 0; ic < inner.length; ic++) {
              _registerCardEl(inner[ic]);
            }
          }

          // Walk UP from the added node to find the card wrapper.
          var parent = node.parentElement;
          var depth  = 0;
          while (parent && depth < 6) {
            if (parent.hasAttribute && parent.hasAttribute('data-product-tags')) {
              _registerCardEl(parent);
              break;
            }
            parent = parent.parentElement;
            depth++;
          }
        }
      }
    };

    // Scope: only the containers where product cards live or get hydrated.
    // Each is checked individually — most pages have only a subset of these.
    // Pages with no card-rendering surface skip the observer entirely.
    //
    // Fallback for variants missed by the observer: preflight() in §4 does a
    // _scanDOMForVariant live lookup if the variant isn't in the registry,
    // and the cart-page audit in §5 is a final backstop. So this scope can
    // miss edge surfaces (e.g., the dcg-top-singles homepage section) without
    // breaking enforcement — those just take a slightly slower preflight path.
    var targets = [
      document.getElementById('CollectionProductGrid'),           // collection page grid
      document.getElementById('shopify-section-advanced_search'), // advanced search results
      document.querySelector('.dcg-related-products-section'),    // related products on product pages
      document.querySelector('.list-view-items.products-display') // search results page
    ].filter(Boolean);

    if (targets.length === 0) return;

    targets.forEach(function (target) {
      new MutationObserver(observerCallback)
        .observe(target, { childList: true, subtree: true });
    });
  }

  /* ═══════════════════════════════════════════════════════════════════════
     UTILITY
     ═══════════════════════════════════════════════════════════════════════ */

  function _closestByClass(el, className) {
    if (el.closest) return el.closest('.' + className);
    var node = el.parentElement;
    while (node) {
      if (node.classList && node.classList.contains(className)) return node;
      node = node.parentElement;
    }
    return null;
  }

  /* ═══════════════════════════════════════════════════════════════════════
     INIT
     ═══════════════════════════════════════════════════════════════════════ */

  function init() {
    buildRegistry();
    _showCarriedNotice();
    // v4: every page checks the cart for $0.00 lines; the cart page then runs
    // its quantity audit (§5) as before.
    auditOnLoad();
    document.addEventListener('cart:refresh', buildRegistry);
    document.addEventListener('cart:updated', scheduleDrawerAudit);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* ═══════════════════════════════════════════════════════════════════════
     PUBLIC API
     ═══════════════════════════════════════════════════════════════════════ */
  return {
    preflight:           preflight,
    auditCart:           auditOnLoad,     // v5: the page-load audit, for the cart page to run after a change
    registerVariant:     registerVariant,
    showToast:           showToast,
    isPricePending:      isPricePending,
    PRICE_PENDING_MESSAGE: PRICE_PENDING_MESSAGE,
    _lookupVariant:      lookupVariant,
    _auditZeroPrice:     auditZeroPrice,
    isInStoreOnly:       isInStoreOnly,       // v6
    _auditInStoreOnly:   auditInStoreOnly,    // v6
    _getRegistry:        function () { return Object.assign({}, _registry); },
    _parseLimitFromTags: parseLimitFromTags,
    _scanDOMForVariant:  _scanDOMForVariant
  };

})();